import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  FiShield, FiLock, FiMonitor, FiClock, FiActivity, 
  FiRefreshCw, FiTrash2, FiUserX, FiSettings,
  FiRadio, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight,
  FiDatabase
} from 'react-icons/fi';
import api from '../../utils/api';
import { API_BASE_URL } from '../../utils/constants';
import { showToast } from '../../utils/toast';
import useConfirm from '../../hooks/useConfirm';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import RecordUsageModal from '../../components/RecordUsageModal/RecordUsageModal';
import './Admin.scss';

// Parse a raw user-agent string into { device, browser }
const parseUserAgent = (ua) => {
  if (!ua || ua === 'unknown') return { device: 'Unknown', browser: 'Unknown' };
  let device = 'Unknown';
  let browser = 'Unknown';
  if (/android/i.test(ua)) device = 'Android';
  else if (/iphone|ipad|ipod/i.test(ua)) device = 'iOS';
  else if (/windows nt/i.test(ua)) device = 'Windows';
  else if (/macintosh|mac os x/i.test(ua)) device = 'Mac';
  else if (/linux/i.test(ua)) device = 'Linux';
  if (/samsungbrowser/i.test(ua)) browser = 'Samsung Browser';
  else if (/opr\//i.test(ua)) browser = 'Opera';
  else if (/edg\//i.test(ua)) browser = 'Edge';
  else if (/chrome\//i.test(ua)) browser = 'Chrome';
  else if (/firefox\//i.test(ua)) browser = 'Firefox';
  else if (/safari\//i.test(ua)) browser = 'Safari';
  return { device, browser };
};

const getEffectivePlan = (plan, role) => {
  if (role === 'superadmin') return 'Gold';
  if (!plan) return null;
  return plan.charAt(0).toUpperCase() + plan.slice(1);
};

const splitDateTime = (dateStr) => {
  const d = new Date(dateStr);
  return {
    date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString(),
  };
};

const relativeTime = (dateStr) => {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const presenceMeta = (presence) => {
  switch (presence) {
    case 'online': return { label: 'Online', color: '#10b981', dot: '#10b981' };
    case 'idle': return { label: 'Idle', color: '#f59e0b', dot: '#f59e0b' };
    case 'offline': return { label: 'Offline', color: '#9ca3af', dot: '#ef4444' };
    default: return { label: presence, color: '#6b7280', dot: '#6b7280' };
  }
};

const eventBadge = (event) => {
  const map = {
    login: { label: 'Login', cls: 'success' },
    logout: { label: 'Logout', cls: 'danger' },
    page_open: { label: 'Open', cls: 'primary' },
    page_close: { label: 'Close', cls: 'danger' },
    tab_visible: { label: 'Active', cls: 'success' },
    tab_hidden: { label: 'Hidden', cls: 'warning' },
    page_view: { label: 'View', cls: 'info' },
    heartbeat: { label: 'Heartbeat', cls: 'muted' },
  };
  return map[event] || { label: event, cls: 'muted' };
};

const Security = () => {
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('userlog');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    failedLogins24h: 0,
    activeSessions: 0,
    lockedAccounts: 0,
    systemHealth: 'Loading...'
  });
  const [settings, setSettings] = useState({
    maxLoginAttempts: 5,
    lockoutDuration: 15,
    passwordMinLength: 8,
    requireSpecialChar: true,
    requireNumber: true,
    requireUppercase: true,
    sessionTimeout: 24,
    maxActiveSessions: 2,
    twoFactorEnabled: false,
    jwtAlgorithm: 'HS512',
    jwtExpiresIn: '7d'
  });
  const [sessions, setSessions] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [attemptsFilter, setAttemptsFilter] = useState('all');
  const [lockedAccounts, setLockedAccounts] = useState([]);

  // ── User Log (replaces overview) state ──────────────────────────
  const [presence, setPresence] = useState([]);
  const [presenceStats, setPresenceStats] = useState({ onlineCount: 0, idleCount: 0, offlineCount: 0, totalSessions: 0 });
  const [recentLogs, setRecentLogs] = useState([]);
  const [sseConnected, setSseConnected] = useState(false);
  const [liveEnabled, setLiveEnabled] = useState(true);
  const [userLogs, setUserLogs] = useState([]);
  const [userLogsTotal, setUserLogsTotal] = useState(0);
  const [userLogsPage, setUserLogsPage] = useState(1);
  const [userLogsSearch, setUserLogsSearch] = useState('');
  const [userLogsEvent, setUserLogsEvent] = useState('all');
  const [userLogsLoading, setUserLogsLoading] = useState(false);
  const sseRef = useRef(null);
  const pollRef = useRef(null);

  // ── Custom pagination for User Log (presence) & Active Sessions ──
  const [presencePag, setPresencePag] = useState({ page: 1, limit: 10 });
  const [sessionsPag, setSessionsPag] = useState({ page: 1, limit: 10 });

  // ── Record usage modal (per-user counts for 9 modules) ──
  const [recordUsageUser, setRecordUsageUser] = useState(null);
  const openRecordUsage = (sessionOrUser) => {
    if (!sessionOrUser) return;
    const u = sessionOrUser.userId && (sessionOrUser.userId._id || sessionOrUser.userId.email)
      ? sessionOrUser.userId
      : sessionOrUser;
    if (!u || (!u._id && typeof u !== 'string')) return;
    setRecordUsageUser(u);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, settingsRes, sessionsRes, attemptsRes, lockedRes] = await Promise.all([
        api.get('/security/stats'),
        api.get('/security/settings'),
        api.get('/security/all-sessions'),
        api.get('/security/attempts'),
        api.get('/security/locked-accounts')
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      setSessions(sessionsRes.data);
      setAttempts(attemptsRes.data);
      setLockedAccounts(lockedRes.data || []);
    } catch (err) {
      console.error(err);
      showToast('error', 'Failed to fetch security data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserLogs = useCallback(async (page = 1, event = userLogsEvent, search = userLogsSearch) => {
    setUserLogsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (event && event !== 'all') params.set('event', event);
      if (search) params.set('search', search);
      const res = await api.get(`/security/user-logs?${params.toString()}`);
      const data = res.data;
      setUserLogs(data.logs || []);
      setUserLogsTotal(data.total || 0);
      setUserLogsPage(data.page || page);
    } catch (e) {
      console.error(e);
    } finally {
      setUserLogsLoading(false);
    }
  }, [userLogsEvent, userLogsSearch]);

  // initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // fetch paginated logs when tab is userlog or filters change
  useEffect(() => {
    if (activeTab === 'userlog') fetchUserLogs(1, userLogsEvent, userLogsSearch);
  }, [activeTab, userLogsEvent]); // search is handled via debounced effect below

  // debounced search
  useEffect(() => {
    if (activeTab !== 'userlog') return;
    const t = setTimeout(() => fetchUserLogs(1, userLogsEvent, userLogsSearch), 400);
    return () => clearTimeout(t);
  }, [userLogsSearch]); // eslint-disable-line

  // ── SSE for instant low-latency presence ────────────────────────
  const fallbackPoll = useCallback(async () => {
    try {
      const [presRes, logsRes] = await Promise.all([
        api.get('/security/presence'),
        api.get('/security/user-logs?limit=30')
      ]);
      // presence endpoint returns array, build stats client-side
      const pres = Array.isArray(presRes.data) ? presRes.data : [];
      const enriched = pres.map(s => ({
        _id: s._id,
        userId: s.userId,
        ipAddress: s.ipAddress,
        device: s.device,
        currentPage: s.currentPage,
        lastActive: s.lastActive,
        lastHeartbeatAt: s.lastHeartbeatAt,
        lastEvent: s.lastEvent,
        presence: s.presence || 'offline',
      }));
      const online = enriched.filter(p => p.presence === 'online').length;
      const idle = enriched.filter(p => p.presence === 'idle').length;
      const offline = enriched.filter(p => p.presence === 'offline').length;
      setPresence(enriched);
      setPresenceStats({ onlineCount: online, idleCount: idle, offlineCount: offline, totalSessions: enriched.length });
      // logsRes might be {logs:[...]} or array fallback
      const logsData = logsRes.data?.logs || logsRes.data || [];
      if (Array.isArray(logsData)) setRecentLogs(logsData.slice(0, 30));
    } catch {}
  }, []);

  useEffect(() => {
    if (!liveEnabled || activeTab !== 'userlog') {
      if (sseRef.current) { sseRef.current.close(); sseRef.current = null; }
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setSseConnected(false);
      return;
    }

    let es;
    let sseFailed = false;
    try {
      const url = `${API_BASE_URL}/security/user-logs/stream`;
      es = new EventSource(url, { withCredentials: true });
      sseRef.current = es;
      es.onopen = () => setSseConnected(true);
      es.onerror = () => {
        if (!sseFailed) {
          sseFailed = true;
          setSseConnected(false);
          es.close();
          // fallback to polling every 2s
          fallbackPoll();
          pollRef.current = setInterval(fallbackPoll, 2000);
        }
      };
      const handleSnapshot = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.presence) setPresence(data.presence);
          if (data.recentLogs) setRecentLogs(data.recentLogs);
          if (data.stats) setPresenceStats(data.stats);
        } catch {}
      };
      es.addEventListener('snapshot', handleSnapshot);
      es.addEventListener('update', handleSnapshot);
    } catch {
      // EventSource not supported / cross-origin blocked -> polling fallback
      fallbackPoll();
      pollRef.current = setInterval(fallbackPoll, 2000);
    }

    return () => {
      if (es) es.close();
      if (pollRef.current) clearInterval(pollRef.current);
      sseRef.current = null;
      pollRef.current = null;
      setSseConnected(false);
    };
  }, [liveEnabled, activeTab, fallbackPoll]);

  // also poll fallback when sse not connected but live enabled (keep ticking relative times)
  useEffect(() => {
    if (activeTab !== 'userlog') return;
    const t = setInterval(() => {
      // force re-render for relativeTime by bumping state shallowly
      setPresence(prev => [...prev]);
    }, 5000);
    return () => clearInterval(t);
  }, [activeTab]);

  const handleUpdateSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      await api.post('/security/settings', settings);
      showToast('success', 'Security settings updated successfully');
    } catch (err) {
      showToast('error', 'Failed to update settings');
    }
  };

  const handleRevokeSession = async (sessionOrId) => {
    const isSessionObj = sessionOrId && typeof sessionOrId === 'object' && sessionOrId._id;
    const sessionId = isSessionObj ? sessionOrId._id : sessionOrId;
    let session = isSessionObj ? sessionOrId : null;
    if (!session) {
      session = sessions.find(s => s._id === sessionId) || presence.find(p => p._id === sessionId) || null;
    }

    let message = 'Are you sure you want to revoke this session?';
    if (session) {
      const userName = session.userId?.name || session.userName || '';
      const userEmail = session.userId?.email || session.email || '';
      const rawUa = session.device || session.userAgent || '';
      const { device, browser } = parseUserAgent(rawUa);
      const hasDevice = device !== 'Unknown' || browser !== 'Unknown';

      // Build a human-readable identifier: prefer "Name (email)" then device/browser
      const userLabel = userName && userEmail
        ? `"${userName}" (${userEmail})`
        : userName
          ? `"${userName}"`
          : userEmail || '';

      const detailParts = [];
      if (hasDevice) detailParts.push(`${device} / ${browser}`);

      if (userLabel) {
        const suffix = detailParts.length ? ` — ${detailParts.join(' — ')}` : '';
        message = `Are you sure you want to revoke ${userLabel}'s session${suffix}?`;
      } else if (detailParts.length) {
        message = `Are you sure you want to revoke this session (${detailParts.join(' — ')})?`;
      }
      // Fallback keeps generic message if no identifiable info
    }

    const confirmed = await confirm(message, {
      title: 'Revoke Session',
      confirmText: 'Revoke',
      showWarning: true,
    });
    if (!confirmed) return;
    try {
      await api.delete(`/security/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s._id !== sessionId));
      setPresence(prev => prev.filter(p => p._id !== sessionId));
      showToast('success', 'Session revoked');
      setStats(prev => ({ ...prev, activeSessions: Math.max(0, prev.activeSessions - 1) }));
    } catch (err) {
      showToast('error', 'Failed to revoke session');
    }
  };

  const handleUnlockUser = async (email) => {
    try {
      await api.post('/security/unlock-user', { email });
      showToast('success', `User ${email} has been unlocked`);
      fetchData();
    } catch (err) {
      showToast('error', 'Failed to unlock user');
    }
  };

  const handleStatClick = (statType) => {
    switch(statType) {
      case 'failed':
        setAttemptsFilter('failed');
        setActiveTab('attempts');
        break;
      case 'sessions':
        setActiveTab('sessions');
        break;
      case 'locked':
        setAttemptsFilter('locked');
        setActiveTab('attempts');
        break;
      default:
        break;
    }
  };

  const filteredAttempts = attempts.filter(attempt => {
    if (attemptsFilter === 'all') return true;
    if (attemptsFilter === 'failed') return attempt.status === 'failed';
    if (attemptsFilter === 'locked') return attempt.reason === 'account_locked' || attempt.reason === 'max_attempts_reached';
    return true;
  });

  // ── Pagination helpers (client-side) ──
  const presenceTotal = presence.length;
  const presencePages = Math.max(1, Math.ceil(presenceTotal / presencePag.limit));
  const sessionsTotal = sessions.length;
  const sessionsPages = Math.max(1, Math.ceil(sessionsTotal / sessionsPag.limit));

  const paginatedPresence = React.useMemo(() => {
    const start = (presencePag.page - 1) * presencePag.limit;
    return presence.slice(start, start + presencePag.limit);
  }, [presence, presencePag]);

  const paginatedSessions = React.useMemo(() => {
    const start = (sessionsPag.page - 1) * sessionsPag.limit;
    return sessions.slice(start, start + sessionsPag.limit);
  }, [sessions, sessionsPag]);

  // clamp pages when data changes
  useEffect(() => {
    if (presencePag.page > presencePages) setPresencePag(p => ({ ...p, page: presencePages }));
  }, [presencePages, presencePag.page]);
  useEffect(() => {
    if (sessionsPag.page > sessionsPages) setSessionsPag(p => ({ ...p, page: sessionsPages }));
  }, [sessionsPages, sessionsPag.page]);

  const handlePresencePage = (p) => setPresencePag(prev => ({ ...prev, page: Math.min(Math.max(1, p), presencePages) }));
  const handlePresenceLimit = (l) => setPresencePag({ page: 1, limit: l });
  const handleSessionsPage = (p) => setSessionsPag(prev => ({ ...prev, page: Math.min(Math.max(1, p), sessionsPages) }));
  const handleSessionsLimit = (l) => setSessionsPag({ page: 1, limit: l });

  const Pagination = ({ page, pages, total, limit, onPage, onLimit }) => (
    <div className="admin-pagination" style={{ marginTop: '0.75rem' }}>
      <div className="admin-pagination__info">
        Showing {total === 0 ? 0 : ((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} results
      </div>
      <div className="admin-pagination__controls">
        <button className="admin-pagination__btn" disabled={page === 1} onClick={() => onPage(1)} title="First"><FiChevronsLeft /> First</button>
        <button className="admin-pagination__btn" disabled={page === 1} onClick={() => onPage(page - 1)}><FiChevronLeft /> Prev</button>
        <span className="admin-pagination__page-info">Page {page} of {pages}</span>
        <button className="admin-pagination__btn" disabled={page === pages} onClick={() => onPage(page + 1)}>Next <FiChevronRight /></button>
        <button className="admin-pagination__btn" disabled={page === pages} onClick={() => onPage(pages)} title="Last">Last <FiChevronsRight /></button>
      </div>
      <div className="admin-pagination__size">
        <label>Per page:</label>
        <CustomSelect
          value={limit}
          onChange={onLimit}
          options={[
            { value: 5, label: '5' },
            { value: 10, label: '10' },
            { value: 25, label: '25' },
            { value: 50, label: '50' },
          ]}
        />
      </div>
    </div>
  );

  const StatCard = ({ icon: Icon, label, value, color, onClick, active }) => (
    <div 
      className={`security-stat-card ${onClick ? 'security-stat-card--clickable' : ''} ${active ? 'security-stat-card--active' : ''}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <div className={`security-stat-card__icon ${color}`}>
        <Icon />
      </div>
      <div className="security-stat-card__content">
        <h4>{value}</h4>
        <p>{label}</p>
      </div>
    </div>
  );

  return (
    <div className="admin-security">
      <div className="admin-page-header">
        <div className="admin-page-header__left">
          <h2>Security Center</h2>
          <p>Monitor system health and manage access controls</p>
        </div>
        <div className="admin-page-header__right">
          <button className="btn btn--outline" onClick={() => { fetchData(); fetchUserLogs(userLogsPage); fallbackPoll(); }} disabled={loading}>
            <FiRefreshCw className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="security-stats-grid">
        <StatCard 
          icon={FiUserX} 
          label="Failed Logins (24h)" 
          value={stats.failedLogins24h} 
          color="danger"
          onClick={() => handleStatClick('failed')}
          active={activeTab === 'attempts' && attemptsFilter === 'failed'}
        />
        <StatCard 
          icon={FiMonitor} 
          label="Active Sessions" 
          value={stats.activeSessions} 
          color="primary"
          onClick={() => handleStatClick('sessions')}
          active={activeTab === 'sessions'}
        />
        <StatCard 
          icon={FiLock} 
          label="Locked Accounts" 
          value={stats.lockedAccounts} 
          color="warning"
          onClick={() => handleStatClick('locked')}
          active={activeTab === 'attempts' && attemptsFilter === 'locked'}
        />
        <StatCard 
          icon={FiShield} 
          label="System Status" 
          value={stats.systemHealth} 
          color="success" 
        />
      </div>

      <div className="admin-tabs">
        <button 
          className={`tab-btn ${activeTab === 'userlog' ? 'active' : ''}`}
          onClick={() => setActiveTab('userlog')}
        >
          <FiRadio /> User Log {liveEnabled && sseConnected && <span style={{marginLeft:6, width:8, height:8, borderRadius:'50%', background:'#10b981', display:'inline-block', boxShadow:'0 0 0 4px rgba(16,185,129,0.2)'}} />}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'sessions' ? 'active' : ''}`}
          onClick={() => setActiveTab('sessions')}
        >
          <FiMonitor /> Active Sessions
        </button>
        <button 
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <FiSettings /> Security Settings
        </button>
        <button 
          className={`tab-btn ${activeTab === 'attempts' ? 'active' : ''}`}
          onClick={() => setActiveTab('attempts')}
        >
          <FiClock /> Login History
        </button>
      </div>

      <div className="security-content">
        {activeTab === 'userlog' && (
          <div className="security-tab-panel">
            {/* Live presence header */}
            <div className="userlog-topbar">
              <div className="userlog-topbar__main">
                <div className="userlog-topbar__live">
                  <span style={{fontSize:'0.85rem', fontWeight:600, color: sseConnected ? '#10b981' : '#6b7280'}}>
                    <FiActivity style={{marginRight:4}} /> {sseConnected ? 'Live' : liveEnabled ? 'Connecting…' : 'Paused'}
                  </span>
                  {sseConnected && <span style={{width:8,height:8, borderRadius:'50%', background:'#10b981', display:'inline-block', animation:'pulse 1.5s infinite'}} />}
                </div>
                <div className="userlog-topbar__actions">
                  <label style={{display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.85rem', cursor:'pointer'}}>
                    <input type="checkbox" checked={liveEnabled} onChange={e=>setLiveEnabled(e.target.checked)} /> Live
                  </label>
                  <button className="btn btn--outline btn--small" onClick={fallbackPoll}> Refresh now</button>
                </div>
              </div>
              <div className="userlog-topbar__badges">
                <span className="admin-badge" style={{background:'rgba(16,185,129,0.12)', color:'#065f46'}}>● Online: {presenceStats.onlineCount}</span>
                <span className="admin-badge" style={{background:'rgba(245,158,11,0.12)', color:'#92400e'}}>● Idle: {presenceStats.idleCount}</span>
                <span className="admin-badge" style={{background:'rgba(156,163,175,0.2)', color:'#374151'}}>● Offline: {presenceStats.offlineCount}</span>
                <span className="admin-badge" style={{background:'var(--color-surface-muted)', color:'var(--color-text-secondary)'}}>Total sessions: {presenceStats.totalSessions}</span>
              </div>
            </div>
            <div className="admin-table-container" style={{marginBottom:'0.75rem'}}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Presence</th>
                    <th>Current Page</th>
                    <th>Device / Browser</th>
                    <th>Last Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPresence.length > 0 ? paginatedPresence.map(s => {
                    const { device, browser } = parseUserAgent(s.device);
                    const pm = presenceMeta(s.presence);
                    const effectivePlan = getEffectivePlan(s.userId?.plan, s.userId?.role);
                    return (
                      <tr key={s._id} onClick={() => openRecordUsage(s)} style={{ cursor: 'pointer' }}>
                        <td>
                          <div className="user-info-cell">
                            <strong>{s.userId?.name || 'Unknown'}</strong>
                            <span>{s.userId?.email || 'N/A'}</span>
                            {effectivePlan && <span className="plan-badge">{effectivePlan}</span>}
                          </div>
                        </td>
                        <td>
                          <span style={{display:'inline-flex', alignItems:'center', gap:'0.4rem', padding:'0.25rem 0.6rem', borderRadius:20, fontSize:'0.78rem', fontWeight:700, background: pm.presence==='online'?'rgba(16,185,129,0.12)': pm.presence==='idle'?'rgba(245,158,11,0.14)':'rgba(156,163,175,0.2)', color: pm.color}}>
                            <span style={{width:8,height:8, borderRadius:'50%', background: pm.dot, display:'inline-block'}} /> {pm.label}
                          </span>
                          {s.lastEvent && <div style={{fontSize:'0.7rem', color:'var(--color-text-faint)', marginTop:2}}>{s.lastEvent}</div>}
                        </td>
                        <td>
                          <code style={{fontSize:'0.82rem', background:'var(--color-surface-inset)', padding:'0.2rem 0.4rem', borderRadius:4}}>{s.currentPage || '/'}</code>
                        </td>
                        <td>
                          <div className="device-info-cell">
                            <span className="device-name">{device}</span>
                            <span className="browser-name">{browser}</span>
                          </div>
                        </td>
                        <td>
                          <div className="datetime-cell">
                            <span>{relativeTime(s.lastHeartbeatAt || s.lastActive)}</span>
                            <span style={{fontSize:'0.75rem', color:'var(--color-text-faint)'}}>{splitDateTime(s.lastActive).time}</span>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'inline-flex', gap: '0.4rem' }}>
                            <button className="btn-icon" onClick={(e) => { e.stopPropagation(); openRecordUsage(s); }} title={`View record usage for ${s.userId?.name || s.userId?.email || 'this user'}`}>
                              <FiDatabase />
                            </button>
                            <button className="btn-icon btn-icon--danger" onClick={(e) => { e.stopPropagation(); handleRevokeSession(s); }} title={`Revoke session for ${s.userId?.name || s.userId?.email || 'this user'}`}>
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="6" className="text-center">No active sessions — users are offline</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {presenceTotal > 0 && (
              <Pagination page={presencePag.page} pages={presencePages} total={presenceTotal} limit={presencePag.limit} onPage={handlePresencePage} onLimit={handlePresenceLimit} />
            )}
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="security-tab-panel">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Device / Browser</th>
                    <th>Last Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSessions.length > 0 ? paginatedSessions.map(session => {
                    const { device, browser } = parseUserAgent(session.device);
                    const effectivePlan = getEffectivePlan(session.userId?.plan, session.userId?.role);
                    const { date, time } = splitDateTime(session.lastActive);
                    return (
                      <tr key={session._id}>
                        <td>
                          <div className="user-info-cell">
                            <strong>{session.userId?.name || 'Unknown'}</strong>
                            <span>{session.userId?.email || 'N/A'}</span>
                            {effectivePlan && <span className="plan-badge">{effectivePlan}</span>}
                          </div>
                        </td>
                        <td>
                          <div className="device-info-cell">
                            <span className="device-name">{device}</span>
                            <span className="browser-name">{browser}</span>
                          </div>
                        </td>
                        <td>
                          <div className="datetime-cell">
                            <span>{date}</span>
                            <span>{time}</span>
                          </div>
                        </td>
                        <td>
                          <button 
                            className="btn-icon btn-icon--danger" 
                            onClick={() => handleRevokeSession(session)}
                            title={`Revoke session for ${session.userId?.name || session.userId?.email || 'this user'}`}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="4" className="text-center">No active sessions found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {sessionsTotal > 0 && (
              <Pagination page={sessionsPag.page} pages={sessionsPages} total={sessionsTotal} limit={sessionsPag.limit} onPage={handleSessionsPage} onLimit={handleSessionsLimit} />
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="security-tab-panel">
            <form className="security-settings-form" onSubmit={handleUpdateSettings}>
              <div className="form-section">
                <h3>Authentication Controls</h3>
                <div className="settings-grid">
                  <div className="form-group">
                    <label>Max Login Attempts</label>
                    <input 
                      type="number" 
                      value={settings.maxLoginAttempts || ''}
                      onChange={(e) => setSettings({...settings, maxLoginAttempts: parseInt(e.target.value) || 0})}
                    />
                    <small>Number of failed attempts before account lockout</small>
                  </div>
                  <div className="form-group">
                    <label>Lockout Duration (Minutes)</label>
                    <input 
                      type="number" 
                      value={settings.lockoutDuration || ''}
                      onChange={(e) => setSettings({...settings, lockoutDuration: parseInt(e.target.value) || 0})}
                    />
                    <small>How long the account remains locked</small>
                  </div>
                  <div className="form-group">
                    <label>Session Timeout (Hours)</label>
                    <input 
                      type="number" 
                      value={settings.sessionTimeout || ''}
                      onChange={(e) => setSettings({...settings, sessionTimeout: parseInt(e.target.value) || 0})}
                    />
                    <small>How long before an idle session automatically expires</small>
                  </div>
                  <div className="form-group">
                    <label>Max Active Sessions Per User</label>
                    <input 
                      type="number"
                      min="1"
                      max="10"
                      value={settings.maxActiveSessions ?? 2}
                      onChange={(e) => setSettings({...settings, maxActiveSessions: parseInt(e.target.value) || 1})}
                    />
                    <small>Maximum simultaneous logins allowed per account (e.g. 2)</small>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>JWT Token Configuration</h3>
                <div className="settings-grid">
                  <div className="form-group">
                    <label>JWT Algorithm</label>
                    <CustomSelect
                      value={settings.jwtAlgorithm || 'HS512'}
                      onChange={(val) => setSettings({...settings, jwtAlgorithm: val})}
                      options={[
                        { value: 'HS256', label: 'HS256 (Standard)' },
                        { value: 'HS384', label: 'HS384 (Enhanced)' },
                        { value: 'HS512', label: 'HS512 (Maximum Security)' },
                      ]}
                    />
                    <small>Higher algorithms provide stronger encryption</small>
                  </div>
                  <div className="form-group">
                    <label>Token Expiration</label>
                    <CustomSelect
                      value={settings.jwtExpiresIn || '7d'}
                      onChange={(val) => setSettings({...settings, jwtExpiresIn: val})}
                      options={[
                        { value: '1h', label: '1 Hour' },
                        { value: '6h', label: '6 Hours' },
                        { value: '12h', label: '12 Hours' },
                        { value: '24h', label: '24 Hours' },
                        { value: '3d', label: '3 Days' },
                        { value: '7d', label: '7 Days (Recommended)' },
                        { value: '14d', label: '14 Days' },
                        { value: '30d', label: '30 Days' },
                      ]}
                    />
                    <small>Shorter duration = more secure, but users login more often</small>
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn--primary">Save Security Policy</button>
              </div>
            </form>
          </div>
        )}

        {activeTab === 'attempts' && (
          <div className="security-tab-panel">
            <div className="security-filter-bar">
              <span className="filter-label">Filter:</span>
              <button 
                className={`filter-btn ${attemptsFilter === 'all' ? 'active' : ''}`}
                onClick={() => setAttemptsFilter('all')}
              >
                All
              </button>
              <button 
                className={`filter-btn ${attemptsFilter === 'failed' ? 'active' : ''}`}
                onClick={() => setAttemptsFilter('failed')}
              >
                Failed Only
              </button>
              <button 
                className={`filter-btn ${attemptsFilter === 'locked' ? 'active' : ''}`}
                onClick={() => setAttemptsFilter('locked')}
              >
                Locked Accounts
              </button>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Device / Browser</th>
                    <th>Status</th>
                    <th>Reason</th>
                    <th>Timestamp</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttempts.length > 0 ? filteredAttempts.map(attempt => {
                    const { device, browser } = parseUserAgent(attempt.userAgent);
                    const effectivePlan = getEffectivePlan(attempt.userPlan, attempt.userRole);
                    const { date, time } = splitDateTime(attempt.timestamp);
                    return (
                      <tr key={attempt._id}>
                        <td>
                          <div className="user-info-cell">
                            {attempt.userName
                              ? <strong>{attempt.userName}</strong>
                              : <strong className="text-muted">Unknown User</strong>
                            }
                            <span>{attempt.email}</span>
                            {effectivePlan && <span className="plan-badge">{effectivePlan}</span>}
                          </div>
                        </td>
                        <td>
                          <div className="device-info-cell">
                            <span className="device-name">{device}</span>
                            <span className="browser-name">{browser}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`status-badge status-badge--${attempt.status === 'success' ? 'active' : 'inactive'}`}>
                            {attempt.status}
                          </span>
                        </td>
                        <td>{attempt.reason || '-'}</td>
                        <td>
                          <div className="datetime-cell">
                            <span>{date}</span>
                            <span>{time}</span>
                          </div>
                        </td>
                        <td>
                          {(attempt.reason === 'account_locked' || attempt.reason === 'max_attempts_reached') && (
                            <button 
                              className="btn btn--xs btn--outline"
                              onClick={() => handleUnlockUser(attempt.email)}
                            >
                              Unlock
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan="6" className="text-center">
                      {attemptsFilter === 'all' ? 'No login history available' : `No ${attemptsFilter} login attempts found`}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      {recordUsageUser && (
        <RecordUsageModal user={recordUsageUser} onClose={() => setRecordUsageUser(null)} />
      )}
    </div>
  );
};

export default Security;
